import Axios from 'axios';
import * as mwx from '@/utils/common.js';
import {
  Toast,
  getDouyinData,
  getWxData,
  uploadLogFile,
  setToken,
  getToken,
  formatLogTime,
  confirmModal,
  loadJsSdk,
  createId
} from './common';
import { baseURL, platform, isDev } from './config';
import { miniAppWebBridgeMap } from './constant.js';
import { logger } from './logger';
import processRequestData from './processRequestData';
import Store from './Store';
import {
  classifyApiError,
  classifyApiResponse,
  createApiObservation,
  finishApiObservation
} from './stability';

const lang = 'zh';

function checkIfNeedWriteLog(url) {
  return [
    'v5/media/upload',
    'v5/media/upload/base64',
    'v5/image/upload',
    'v5/image/upload/base64',
    'v5/image/segment/url',
    'v5/image/crop/alpha'
  ].filter((v) => url.indexOf(v) > -1 || v.indexOf(url) > -1).length;
}

function n(fn) {
  try {
    fn();
  } catch (error) {
    console.error(error);
    // 处理错误
  }
}

Axios.defaults.withCredentials = true;
var instance = Axios.create({
  baseURL,
  timeout: 60 * 1000,
  withCredentials: false,
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
});

let timeoutList = ['ECONNABORTED', 'timeout'];

function isTimeoutError(errMsg) {
  try {
    if (
      timeoutList.filter(
        (v) => v.indexOf(errMsg) > -1 || errMsg.indexOf(v) > -1
      ).length
    ) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

let expiredBearerFlow = null;

/**
 * 判断待处理凭据是否仍属于当前 Bearer 会话，防止异步等待期间切换认证模式后执行旧动作。
 * @param {string} token - 待处理的 token
 * @param {number|string} expiresAt - 待处理凭据的绝对毫秒到期时刻
 * @returns {boolean} 当前认证类型与凭据是否仍匹配
 */
function isCurrentBearerToken(token, expiresAt) {
  const wxData = getWxData();
  return !!(
    wxData &&
    wxData.authType === 'Bearer' &&
    getToken() === token &&
    wxData.expiresAt === expiresAt
  );
}

/**
 * 仅为当前 Bearer 凭据合并重连交互；确认时通知小程序刷新 token，两种选择均返回小程序。
 * @param {string} token - 当前请求使用的 token，用于区分重新进入后的凭据
 * @param {number|string} expiresAt - 小程序传入的绝对毫秒到期时刻，用于区分续期后的凭据
 * @returns {Promise<void>} 弹窗选择及桥接调用完成；SDK 或桥接失败时拒绝并允许重试
 */
function handleExpiredBearerToken(token, expiresAt) {
  if (!isCurrentBearerToken(token, expiresAt)) {
    return Promise.resolve();
  }
  if (
    !expiredBearerFlow ||
    !expiredBearerFlow.promise ||
    expiredBearerFlow.token !== token ||
    expiredBearerFlow.expiresAt !== expiresAt
  ) {
    const flow = { token, expiresAt, promise: null };
    expiredBearerFlow = flow;
    mwx.hideLoading();
    flow.promise = confirmModal({
      content: '连接已中断，是否重连？',
      showCancel: true,
      confirmText: '确认',
      cancelText: '取消'
    })
      .then(async (result) => {
        if (!isCurrentBearerToken(token, expiresAt)) {
          return;
        }
        // 首次请求可能早于 SDK 加载完成，返回前等待微信桥接方法可用。
        await loadJsSdk('wx');
        if (!isCurrentBearerToken(token, expiresAt)) {
          return;
        }
        if (result === 1) {
          window.wx.miniProgram.postMessage({
            data: { type: miniAppWebBridgeMap.refreshToken }
          });
        }
        await new Promise((resolve, reject) => {
          window.wx.miniProgram.navigateBack({
            delta: 1,
            success: resolve,
            fail: reject
          });
        });
      })
      .catch((error) => {
        if (expiredBearerFlow === flow) {
          // 桥接失败仍保留该凭据已失效的标记，后续请求只能重试重连，不能再次发往服务端。
          expiredBearerFlow.promise = null;
        }
        throw error;
      })
      .finally(() => {
        if (
          expiredBearerFlow === flow &&
          !isCurrentBearerToken(token, expiresAt)
        ) {
          expiredBearerFlow = null;
        }
      });
  }
  // 返回动作后仍保留 Promise，防止页面销毁前的轮询或重试再次弹窗。
  return expiredBearerFlow.promise;
}

/**
 * 拦截 HTTP 401 的 Bearer 响应，复用到期重连流程；旧凭据的迟到响应只取消原请求。
 * @param {Object} response - 原始 Axios 响应，含发送请求时保存的凭据快照
 * @returns {Promise<void>} 当前非 Bearer 时不处理；当前 Bearer 的旧请求在重连处理后取消
 */
async function handleUnauthorizedBearerResponse(response) {
  if (response.status !== 401) {
    return;
  }
  const config = response.config;
  const bearerAuth = config && config.miniAppBearerAuth;
  if (!bearerAuth) {
    return;
  }
  const wxData = getWxData();
  if (!wxData || wxData.authType !== 'Bearer') {
    return;
  }
  if (isCurrentBearerToken(bearerAuth.token, bearerAuth.expiresAt)) {
    await handleExpiredBearerToken(bearerAuth.token, bearerAuth.expiresAt);
  }
  throw new Axios.CanceledError('Bearer token 已过期，请重新连接', config);
}

let errorConfig = {};
instance.interceptors.request.use(
  /**
   * 请求发送前校验 Bearer 凭据有效期，再补齐公共参数及对应的认证请求头。
   * @param {Object} config - Axios 请求配置
   * @returns {Promise<Object>} 有效请求配置；过期时完成重连交互并取消请求
   */
  async (config) => {
    let wxData = getWxData();
    const token = getToken();
    // 固定请求发送时的凭据，避免旧请求的 401 响应触发新会话的重连。
    const bearerAuth =
      wxData && wxData.authType === 'Bearer'
        ? { token, expiresAt: wxData.expiresAt }
        : null;
    config.miniAppBearerAuth = bearerAuth;
    if (bearerAuth) {
      // 只有 authType 是 Bearer类型才校验
      const expiresAt = Number(bearerAuth.expiresAt);
      const alreadyExpired =
        expiredBearerFlow &&
        expiredBearerFlow.token === token &&
        expiredBearerFlow.expiresAt === bearerAuth.expiresAt;
      // expiresAt 是绝对毫秒到期时刻；HTTP 401 已判失效的凭据也必须阻止发送。
      if (
        !Number.isFinite(expiresAt) ||
        Date.now() >= expiresAt ||
        alreadyExpired
      ) {
        await handleExpiredBearerToken(token, bearerAuth.expiresAt);
        throw new Axios.CanceledError(
          'Bearer token 已过期，请重新连接',
          config
        );
      }
    }

    n(() => {
      config.metadata = {
        startTime: Date.now(),
        stabilityObservation: createApiObservation(config)
      };
    });

    if (!config.url.match(/lang=/g)) {
      if (!config.url.match(/\?/g)) {
        config.url += '?lang=' + lang;
      } else {
        config.url += '&lang=' + lang;
      }
    }
    let deviceId = Store.get('deviceId') || '';
    if (!deviceId) {
      deviceId = createId(32);
      Store.set('deviceId', deviceId);
    }
    if (!config.url.match(/deviceId=/g)) {
      if (!config.url.match(/\?/g)) {
        config.url += '?deviceId=' + deviceId;
      } else {
        config.url += '&deviceId=' + deviceId;
      }
    }
    let dyData = getDouyinData();
    if (bearerAuth) {
      // Bearer 模式替换旧 token 请求头，避免同时发送两种认证信息。
      config.headers.delete('token');
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      config.headers.token = token;
    }
    let newPlatform = platform;
    if (dyData && dyData.platform) {
      newPlatform = dyData.platform;
    } else if (wxData && wxData.platform) {
      newPlatform = wxData.platform;
    } else {
      let arr = location.href.match(/&platform=(.*)&?/);
      if (arr && arr[1] && typeof parseInt(arr[1]) == 'number') {
        newPlatform = parseInt(arr[1]);
      }
    }
    if (!config.url.match(/platform=/g)) {
      if (!config.url.match(/\?/g)) {
        config.url += '?platform=' + newPlatform;
      } else {
        config.url += '&platform=' + newPlatform;
      }
    }
    if (!config.url.match(/timestamp=/g)) {
      let timestamp = +new Date();
      if (!config.url.match(/\?/g)) {
        config.url += '?timestamp=' + timestamp;
      } else {
        config.url += '&timestamp=' + timestamp;
      }
    }
    errorConfig = {
      url: config.url,
      data: config.data
    };
    // 在发送请求之前做些什么
    return config;
  },
  (error) => {
    // 对请求错误做些什么
    return Promise.reject(error);
  }
);

// 在业务响应解析前处理 HTTP 401，同时覆盖自定义 validateStatus 放行的响应。
instance.interceptors.response.use(
  async (response) => {
    await handleUnauthorizedBearerResponse(response);
    return response;
  },
  async (error) => {
    if (error && error.response) {
      await handleUnauthorizedBearerResponse(error.response);
    }
    throw error;
  }
);

const reportApiList = [
  'v5/media/upload',
  'v5/image/upload',
  'v5/image/segment/url'
];

instance.interceptors.response.use(
  (data) => {
    n(() => {
      finishApiObservation(
        data.config &&
          data.config.metadata &&
          data.config.metadata.stabilityObservation,
        classifyApiResponse(data)
      );
    });
    n(() => {
      if (checkIfNeedWriteLog(data.config.url)) {
        const duration = Math.round(
          (Date.now() - data.config.metadata.startTime) / 1000
        );
        // logger.info(`接口url=${data.config.url} 耗时: ${duration}s`);
        try {
          window.report &&
            window.report(`api_${data.config.url.split('?')[0]}`, {
              time: formatLogTime(Date.now() - data.config.metadata.startTime)
            });
        } catch (error) {
          console.log(error);
        }
      }
    });

    if (data.config.disableErrorToast) {
      data.disableErrorToast = true;
      data.data && (data.data.disableErrorToast = true);
    }
    if (data.status == 200 && data.data && !data.data.error) {
      n(() => {
        try {
          let url, hash;
          try {
            url = data.config.url.split('?')[0];
            hash = location.hash.split('?')[0];
          } catch (error) {}
          // if (data.config.url.indexOf('check') > -1) {
          //   debugger;
          //   data.data = {
          //     code: 1009,
          //     data: '',
          //     message: 'content risky'
          //   };
          // }
          if ([0, 1001].indexOf(data.data.code) == -1) {
            logger.info(
              `[${hash}]:报错,url=${url},req=${JSON.stringify(
                processRequestData(data.config.data)
              )},res=${JSON.stringify(data.data)}`
            );
          } else {
            let report = false;
            reportApiList.forEach((v) => {
              if (data.config.url.indexOf(v) > -1) {
                report = true;
              }
            });
            let reqData = `req=${JSON.stringify(
              processRequestData(data.config.data)
            )}`;

            let ignoreReqUrlList = ['/v5/image/upload', '/v5/media/upload'];
            if (ignoreReqUrlList.some((v) => data.config.url.indexOf(v) > -1)) {
              reqData = '';
            }
            report &&
              logger.info(
                `[${hash}]:url=${url},${reqData},res=${JSON.stringify(
                  data.data
                )}`
              );
          }
        } catch (error) {
          console.error(error);
          // 处理错误
        }
      });
      return data.data;
    } else {
      logger.info(`接口报错,response=${JSON.stringify(data)}`);
      return Promise.reject(data);
    }
  },
  (error) => {
    n(() => {
      const observation =
        error &&
        error.config &&
        error.config.metadata &&
        error.config.metadata.stabilityObservation;
      finishApiObservation(observation, classifyApiError(error, Axios));
    });
    return Promise.reject(error);
  }
);

let whiteList = [];
const ignoreMsgList = ['invalid_png'];
instance.interceptors.response.use((res) => {
  return new Promise((resolve, reject) => {
    if (res.code == 0) {
      resolve(res.data);
    } else if (res.code == 1000) {
      reject(res.data);
      errorConfig.res = res;
    } else if (res.code == 403) {
      window.report && window.report('token_invalid');
      logger.info('登录信息失效');
      setToken('');
      if (Store.getSession('isOid') == 1) {
        Toast('登录信息失效,退出重试');
      } else {
        let dyData = getDouyinData();
        let wxData = getWxData();
        let isDouyinApp = dyData && dyData.orderNo;
        let isWx =
          location.href.indexOf('isFromMiniApp') > -1 &&
          window.wx &&
          window.wx.miniProgram;
        let sdk = isDouyinApp ? window.tt : window.wx;
        if (isWx || isDouyinApp || wxData) {
          sdk.miniProgram.postMessage({
            data: {
              type: miniAppWebBridgeMap.reLogin
            }
          });
          sdk.miniProgram.navigateBack();
        } else {
          location.href = location.origin;
          window.report &&
            window.report('error', {
              reason: 'code=403'
            });
        }
      }
    } else if (whiteList.indexOf(res.code) > -1) {
      resolve(res);
    } else {
      reject(res);
      errorConfig.res = res;
    }
  })
    .then((res) => {
      errorConfig = {};
      return res;
    })
    .catch(async (err) => {
      // logger.error('请求错误errorConfig', errorConfig);
      // logger.error(err);
      if (err && err.code) {
        if (err.code != 1001 && err.code !== 1009 && !err.disableErrorToast) {
          Toast(`连接超时,code=${err.code}`);
          //
        }
        if (err.code == 2000) {
          uploadLogFile(true);
        }
      } else if (typeof err === 'string') {
        if (isTimeoutError(err)) {
          await confirmModal({
            content: '网络连接超时，请检查网络环境后重试(关闭VPN或代理)',
            showCancel: false,
            confirmText: '确定'
          });
        } else {
          if (ignoreMsgList.indexOf(err) == -1) {
            Toast(err);
          }
        }
      }
      throw err;
    });
});

export default instance;
